import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatPhone } from '../utils/formatters';

interface Attendance {
  id: number;
  person_name: string;
  person_type: 'Parent' | 'Staff' | 'Guest';
  checkin_method: 'QR' | 'MANUAL';
  checked_in_at: string;
}

interface Parent {
  id: number;
  name: string;
}

interface Staff {
  id: number;
  name: string;
}

interface AttendanceModalProps {
  type: 'meetings' | 'events' | 'bible-studies';
  itemId: number;
  title: string;
  onClose: () => void;
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({ type, itemId, title, onClose }) => {
  const [attendees, setAttendees] = useState<Attendance[]>([]);
  const [allParents, setAllParents] = useState<Parent[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  
  const [activeTab, setActiveTab] = useState<'parent' | 'staff' | 'guest'>('parent');
  const [selectedParent, setSelectedParent] = useState<number | ''>('');
  const [selectedStaff, setSelectedStaff] = useState<number | ''>('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [attendanceRes, parentsRes, staffRes] = await Promise.all([
          api.get(`${type}/${itemId}/attendance/`),
          api.get('parents/'),
          api.get('staff/')
        ]);
        setAttendees(attendanceRes.data);
        setAllParents(parentsRes.data);
        setAllStaff(staffRes.data);
      } catch (error) {
        console.error('Failed to fetch data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [type, itemId]);

  const handleAddAttendance = async () => {
    let payload = {};

    if (activeTab === 'parent') {
      if (!selectedParent) return;
      payload = { parent_id: selectedParent };
    } else if (activeTab === 'staff') {
      if (!selectedStaff) return;
      payload = { staff_id: selectedStaff };
    } else {
      if (!guestName) return;
      payload = { guest_name: guestName, guest_phone: guestPhone };
    }

    try {
      await api.post(`${type}/${itemId}/attendance/`, payload);
      
      // Refresh list
      const response = await api.get(`${type}/${itemId}/attendance/`);
      setAttendees(response.data);
      
      // Reset fields
      setSelectedParent('');
      setSelectedStaff('');
      setGuestName('');
      setGuestPhone('');
    } catch (error) {
      console.error('Failed to add attendance', error);
      alert('Erro ao registrar presença.');
    }
  };

  const handleGuestPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestPhone(formatPhone(e.target.value));
  };

  const getMethodIcon = (method: string) => {
    return method === 'QR' ? '📱' : '📝';
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Parent': return <span className="badge badge-info">Pai/Mãe</span>;
      case 'Staff': return <span className="badge badge-warning">Equipe</span>;
      case 'Guest': return <span className="badge badge-secondary">Convidado</span>;
      default: return null;
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div className="card" style={{ width: '700px', maxWidth: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Lista de Presença</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>
        
        <p style={{ marginBottom: '20px' }}><strong>{title}</strong></p>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '15px' }}>
            <button 
              style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: activeTab === 'parent' ? '2px solid var(--primary-blue)' : 'none', fontWeight: activeTab === 'parent' ? 'bold' : 'normal', cursor: 'pointer' }}
              onClick={() => setActiveTab('parent')}
            >
              Pais
            </button>
            <button 
              style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: activeTab === 'staff' ? '2px solid var(--primary-blue)' : 'none', fontWeight: activeTab === 'staff' ? 'bold' : 'normal', cursor: 'pointer' }}
              onClick={() => setActiveTab('staff')}
            >
              Colaboradores
            </button>
            <button 
              style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: activeTab === 'guest' ? '2px solid var(--primary-blue)' : 'none', fontWeight: activeTab === 'guest' ? 'bold' : 'normal', cursor: 'pointer' }}
              onClick={() => setActiveTab('guest')}
            >
              Convidados
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            {activeTab === 'parent' && (
              <select 
                value={selectedParent} 
                onChange={(e) => setSelectedParent(Number(e.target.value))}
                style={{ flex: 1 }}
              >
                <option value="">Selecionar pai/responsável...</option>
                {allParents
                  .filter(p => !attendees.some(a => a.person_name === p.name && a.person_type === 'Parent'))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))
                }
              </select>
            )}
            
            {activeTab === 'staff' && (
              <select 
                value={selectedStaff} 
                onChange={(e) => setSelectedStaff(Number(e.target.value))}
                style={{ flex: 1 }}
              >
                <option value="">Selecionar colaborador...</option>
                {allStaff
                  .filter(s => !attendees.some(a => a.person_name === s.name && a.person_type === 'Staff'))
                  .map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))
                }
              </select>
            )}

            {activeTab === 'guest' && (
              <>
                <input 
                  type="text" 
                  value={guestName} 
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Nome do convidado"
                  style={{ flex: 1 }}
                />
                <input 
                  type="text" 
                  value={guestPhone} 
                  onChange={handleGuestPhoneChange}
                  placeholder="Telefone"
                  maxLength={15}
                  style={{ flex: 1 }}
                />
              </>
            )}

            <button className="btn" onClick={handleAddAttendance}>
              Adicionar
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
          <table style={{ width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Origem</th>
                <th>Horário</th>
              </tr>
            </thead>
            <tbody>
              {attendees.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                    Nenhuma presença registrada.
                  </td>
                </tr>
              ) : (
                attendees.map(att => (
                  <tr key={att.id}>
                    <td>{att.person_name}</td>
                    <td>{getTypeBadge(att.person_type)}</td>
                    <td>
                      <span title={att.checkin_method === 'QR' ? 'QR Code' : 'Manual'}>
                        {getMethodIcon(att.checkin_method)} {att.checkin_method === 'QR' ? 'QR Code' : 'Manual'}
                      </span>
                    </td>
                    <td>{new Date(att.checked_in_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Total: {attendees.length}</span>
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;
